import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { useLang } from "@/contexts/LanguageContext";
import { staticImg } from "@/lib/api";

export default function ProsPage() {
  const { t } = useLang();
  return (
    <div data-testid="pros-page">
      {/* HERO */}
      <Section testid="pros-hero" className="pt-32 md:pt-36">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <Reveal><Eyebrow>{t.nav.pros}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] mt-6 whitespace-pre-line">
                  {t.pros.hero_h1}
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-2xl text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                  {t.pros.hero_sub}
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="mt-9 flex flex-wrap gap-4">
                  <Link to="/contact" className="btn-gold" data-testid="pros-hero-cta">{t.pros.cta_btn} <ArrowRight size={14} /></Link>
                  <Link to="/rendez-vous" className="btn-outline-gold">{t.common.appointment}</Link>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.25} className="lg:col-span-6">
              <div className="relative overflow-hidden rounded-2xl aspect-[4/5] kbd-card">
                <img src={staticImg("/api/static/generated/b2b_showroom.png")} onError={(e)=>{e.currentTarget.src=staticImg("/api/static/generated/collection_obscura.png")}} alt="Showroom B2B" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* WHY */}
      <Section className="bg-arven-surface border-y border-white/5">
        <Container>
          <Reveal><Eyebrow>{t.pros.why_eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight mt-6 max-w-3xl">
              {t.pros.why_h2}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {t.pros.values.map((v, i) => (
              <Reveal key={v.t} delay={0.05 * i}>
                <div className="p-7 md:p-8 rounded-2xl kbd-card h-full">
                  <div className="flex items-baseline gap-3">
                    <span className="serif text-arven-gold text-2xl">0{i + 1}</span>
                    <span className="hairline flex-1 max-w-[60px]" />
                  </div>
                  <h3 className="serif text-2xl mt-4">{v.t}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-arven-offwhite/70">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* AUDIENCE */}
      <Section testid="pros-audience">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
            <div className="md:col-span-5">
              <Reveal><Eyebrow>{t.pros.audience_eyebrow}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="serif text-3xl md:text-4xl lg:text-[40px] leading-tight mt-6">{t.pros.audience_h2}</h2>
              </Reveal>
            </div>
            <div className="md:col-span-7">
              <Reveal delay={0.2}>
                <ul className="divide-y divide-white/10">
                  {t.pros.audiences.map((a) => (
                    <li key={a} className="flex items-center gap-5 py-5">
                      <Check size={16} className="text-arven-gold" />
                      <span className="text-base md:text-[17px] text-arven-offwhite/85">{a}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section className="bg-arven-anthracite border-t border-white/5 noise-overlay relative">
        <Container>
          <div className="max-w-3xl mx-auto text-center py-6 md:py-12">
            <Reveal>
              <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight whitespace-pre-line">{t.pros.cta_h2}</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex justify-center gap-4 flex-wrap">
                <Link to="/contact" className="btn-gold" data-testid="pros-cta-presentation">{t.pros.cta_btn} <ArrowRight size={14} /></Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </div>
  );
}
